import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSpamFilterToggle } from '../hooks/useLocalStorageToggle';
import { Typography, Box, Tabs, Tab, Paper, Grid } from '@mui/material';
import {
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  Web as WebIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import PageContainer from '../components/PageContainer';
import AnalyticsOverview from '../components/analytics/AnalyticsOverview';
import EmailPerformance from '../components/analytics/EmailPerformance';
import LandingPages from '../components/analytics/LandingPages';
import Engagement from '../components/analytics/Engagement';
import DateRangePicker from '../components/analytics/DateRangePicker';
import { DateRange } from '../services/analyticsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      className="py-4"
      {...other}
    >
      {value === index && (
        <Box>{children}</Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `analytics-tab-${index}`,
    'aria-controls': `analytics-tabpanel-${index}`,
  };
}

const Analytics: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Map tab names to indices
  const tabMap = {
    'overview': 0,
    'email-performance': 1,
    'landing-pages': 2,
    'engagement': 3,
    // 'tags': 4  // TODO: Re-enable in next release
  } as const;

  // Map indices to tab names
  const reverseTabMap = {
    0: 'overview',
    1: 'email-performance',
    2: 'landing-pages',
    3: 'engagement',
    // 4: 'tags'  // TODO: Re-enable in next release
  } as const;

  type TabValue = 0 | 1 | 2 | 3; // TODO: Add | 4 when Tags tab is re-enabled

  const [value, setValue] = useState<TabValue>(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabParam in tabMap) {
      return tabMap[tabParam as keyof typeof tabMap];
    }
    return 0; // Default to overview
  });

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);  // last 30 days
    return {
      date_range: 'last_30_days',
      startDate: start,
      endDate: end
    };
  });

  const [includeSpamChecker, setIncludeSpamChecker] = useSpamFilterToggle();

  const handleDateRangeChange = (newDateRange: DateRange) => {
    // For custom date ranges, use the provided dates
    if (newDateRange.date_range === 'custom') {
      setDateRange(newDateRange);
      return;
    }

    // Calculate dates based on range type
    const end = new Date();
    const start = new Date();

    switch (newDateRange.date_range) {
      case 'last_7_days':
        start.setDate(end.getDate() - 7);
        break;
      case 'last_30_days':
        start.setDate(end.getDate() - 30);
        break;
      case 'last_90_days':
        start.setDate(end.getDate() - 90);
        break;
      case 'last_12_months':
        start.setMonth(end.getMonth() - 12);
        break;
      default:
        start.setDate(end.getDate() - 30); // default to last 30 days
    }

    // Set time to start of day for start date and end of day for end date
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    setDateRange({
      date_range: newDateRange.date_range,
      startDate: start,
      endDate: end
    });
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setValue(newValue);
    // Update URL with new tab
    const newTab = reverseTabMap[newValue];
    navigate(`/app/analytics?tab=${newTab}`, { replace: true });
  };

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Analytics
      </Typography>

      <Paper elevation={2} className="p-6">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Tabs 
              value={value} 
              onChange={handleChange} 
              variant="scrollable"
              scrollButtons="auto"
              className="bg-white"
            >
              <Tab 
                icon={<BarChartIcon />} 
                iconPosition="start" 
                label="Overview" 
                {...a11yProps(0)} 
                className={value === 0 ? "text-primary-600 font-medium" : ""}
              />
              <Tab 
                icon={<TimelineIcon />} 
                iconPosition="start" 
                label="Email Performance" 
                {...a11yProps(1)} 
                className={value === 1 ? "text-primary-600 font-medium" : ""}
              />
              <Tab 
                icon={<WebIcon />} 
                iconPosition="start" 
                label="Landing Pages" 
                {...a11yProps(2)} 
                className={value === 2 ? "text-primary-600 font-medium" : ""}
              />
              <Tab 
                icon={<PeopleIcon />} 
                iconPosition="start" 
                label="Engagement" 
                {...a11yProps(3)} 
                className={value === 3 ? "text-primary-600 font-medium" : ""}
              />
              {/* TODO: Re-enable Tags tab in next release
              <Tab 
                icon={<LocalOfferIcon />} 
                iconPosition="start" 
                label="Tags" 
                {...a11yProps(4)} 
                className={value === 4 ? "text-primary-600 font-medium" : ""}
              />
              */}
            </Tabs>
            
            <TabPanel value={value} index={0}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                <DateRangePicker dateRange={dateRange} onDateRangeChange={handleDateRangeChange} />
              </Box>
              <AnalyticsOverview 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange}
                includeSpamChecker={includeSpamChecker}
                onSpamCheckerChange={setIncludeSpamChecker}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <EmailPerformance 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange}
                includeSpamChecker={includeSpamChecker}
                onSpamCheckerChange={setIncludeSpamChecker}
              />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <LandingPages 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange} 
              />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <Engagement 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange}
                includeSpamChecker={includeSpamChecker}
                onSpamCheckerChange={setIncludeSpamChecker}
              />
            </TabPanel>
            {/* TODO: Re-enable Tags tab in next release
            <TabPanel value={value} index={4}>
              <TagsAnalytics 
                dateRange={dateRange} 
                onDateRangeChange={handleDateRangeChange} 
              />
            </TabPanel>
            */}
          </Grid>
        </Grid>
      </Paper>
    </PageContainer>
  );
};

export default Analytics;
